import { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { DateField } from '@/components/ui/date-time-field';
import { useT } from '@/i18n/LanguageContext';
import { ItemMasterSearch } from './ItemMasterSearch';
import { CategoryPicker } from './CategoryPicker';
import { MakerPicker } from './MakerPicker';
import { LocationPicker } from './LocationPicker';
import { RequiredHints } from './RequiredHints';
import { EmployeePicker } from './EmployeePicker';
import { AttachmentsField } from './AttachmentsField';
import { FormSection, RequiredMark } from './TechsFields';
import { TechsOrderSection } from './TechsOrderSection';
import { TechsReadinessPanel } from './TechsReadinessPanel';

/**
 * <CommonRegistrationBlock> — どの台帳の「登録」でも同じ部品・同じ並び・同じ名前
 * （UNIFIED_REGISTRATION_SPEC 2.6 ＋ TECHS 整合 2026-06）。
 *
 * かたまりは 4 つ:
 *   1. 基本情報              … 何なのか
 *   2. TECHS発注に必要な情報  … これが埋まっていれば 部品受注入力 →
 *                              TECHS 部品受注入力/部品手配入力 に打ち直し無しで流せる
 *   3. 保管・管理            … どこにあって誰が持っているか
 *   4. 台帳ごとの追加         … 点検・校正など（`extra` / `children`）
 *
 * 項目の有無は `fields`（Set）で台帳ごとに決める。必須の扱いは `requiredKeys`
 * （＊ と 要入力 の対象）と `blocking`（保存を止めるか）で決める。
 * **既存の台帳の必須条件は緩めない。** 新しく足した項目は警告だけ（仕様 4）。
 */

export const ALL_FIELDS = [
  'master_search', 'name', 'number', 'category', 'kind', 'maker', 'model_no', 'maker_pn',
  'serial_no', 'sku_group', 'order_category', 'uom', 'qty', 'supplier', 'reference_url',
  'location', 'department', 'status', 'owner', 'acquired_on', 'price', 'attachments', 'notes',
];

export const FIELD_LABELS = {
  name: '品名', number: '品番 / 資産番号', category: 'カテゴリ', kind: '種別',
  maker: 'メーカー', model_no: '型式・仕様', maker_pn: 'メーカー品番', serial_no: '製造番号',
  sku_group: '品番グループ', order_category: '受注分類', uom: '単位', qty: '数量',
  supplier: '仕入先（注文先）', reference_url: '注文URL', location: '設置場所',
  department: '部門', status: '状態', owner: '使用者・管理者', acquired_on: '取得日',
  price: '取得価格（円）', notes: '備考',
};

const VALUE_OF = {
  number: (v) => v?.number_value?.number || v?.number_value?.template_code || v?.asset_no,
  sku_group: (v) => v?.sku_group_id,
  order_category: (v) => v?.order_category_id,
  location: (v) => v?.location_id || v?.location,
  supplier: (v) => v?.supplier_id || v?.supplier_name || v?.vendor,
  price: (v) => v?.acquisition_price,
};

/** 未入力の必須項目の**ラベル**を返す（警告の文面に使う）。 */
export const missingFields = (value, requiredKeys = []) => requiredKeys
  .filter((k) => {
    const raw = VALUE_OF[k] ? VALUE_OF[k](value) : value?.[k];
    if (k === 'qty') return raw === '' || raw === null || raw === undefined || Number(raw) < 0;
    return !String(raw ?? '').trim();
  })
  .map((k) => FIELD_LABELS[k] || k);

// 2026-10-21 · 「登録の種類」。既存の物には TECHS発注の項目を出さない。
export const ACQUISITION_OPTIONS = [
  ['existing', '既存（すでに社内にある）'],
  ['purchase', 'これから購入する'],
  ['in_house', '自社製作（開発案件から）'],
];
const ORDER_ONLY_FIELDS = ['sku_group', 'order_category', 'uom', 'supplier',
  'reference_url', 'price', 'maker_pn'];

export const CommonRegistrationBlock = ({
  value, onChange, editing = false,
  fields = ALL_FIELDS, requiredKeys = [], extraMissing = [], requiredTitle = null,
  register = 'equip', appCode = '', currentNo = '',
  categories = [], departments = [], employees = [], statusOptions = [],
  locationDepartmentId = null, kindOptions = [],
  deptCode = '',
  onNumberIssued = null,
  files = [], onFilesChange, onUpload,
  techsTitle = undefined, techsNote = undefined,
  askAcquisitionType = false, acquisitionOptions = ACQUISITION_OPTIONS,
  techsContext = '', departmentName = '',
  basicExtra = null, keepingExtra = null,
  extra = null, children, testPrefix = 'reg',
}) => {
  const { t } = useT();
  const [showOrderManual, setShowOrderManual] = useState(false);
  const acq = value?.acquisition_type || 'existing';
  const hasTechsData = !!(value?.order_category_id || value?.supplier_id
    || value?.techs_order_no || value?.acquisition_price || value?.unit_price);
  const showOrder = !askAcquisitionType
    || (editing ? (acq === 'purchase' || hasTechsData || showOrderManual) : acq === 'purchase');
  const effFields = showOrder ? fields : fields.filter((f) => !ORDER_ONLY_FIELDS.includes(f));
  const effRequired = showOrder ? requiredKeys
    : requiredKeys.filter((f) => !ORDER_ONLY_FIELDS.includes(f));
  const has = (k) => effFields.includes(k);
  const req = (k) => effRequired.includes(k);
  const patch = (p) => onChange({ ...value, ...p });
  const set = (k) => (e) => patch({ [k]: e?.target ? e.target.value : e });
  const mark = (k) => <RequiredMark show={req(k) && !editing} />;
  const missing = editing ? [] : [...missingFields(value, effRequired), ...extraMissing];

  return (
    <div className="space-y-3" data-testid={`${testPrefix}-common-block`}>
      {askAcquisitionType && !editing && (
        <div className="rounded-lg border border-zinc-200 bg-zinc-50/70 p-3" data-testid={`${testPrefix}-acq`}>
          <p className="text-xs font-semibold text-zinc-700">登録の種類</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {acquisitionOptions.map(([v, label]) => (
              <button key={v} type="button" aria-pressed={acq === v}
                onClick={() => patch({
                  acquisition_type: v,
                  // 自社製作で入れたメーカーは、種類を変えたら外す（手入力は残す）
                  ...(v === 'in_house'
                    ? (String(value?.maker || '').trim() ? {} : { maker: '自社製作（技術部）' })
                    : (value?.maker === '自社製作（技術部）' ? { maker: '' } : {})),
                })}
                data-testid={`${testPrefix}-acq-${v}`}
                className={`rounded-full border px-3 py-1.5 text-[12px] font-medium transition-colors ${
                  acq === v ? 'border-teal-600 bg-teal-600 text-white'
                    : 'border-zinc-300 bg-white text-zinc-700 hover:border-teal-400'}`}>
                {label}
              </button>
            ))}
          </div>
          <p className="mt-1.5 text-[11px] text-zinc-500" data-testid={`${testPrefix}-acq-note`}>
            {acq === 'purchase'
              ? '発注に必要な項目（品番グループ・受注分類・単位・仕入先・注文URL・単価）も聞きます。そのまま部品受注入力に進めます。'
              : acq === 'in_house'
                ? '開発案件の引き渡しで作るのが普通です。メーカーは「自社製作（技術部）」になります。'
                : 'すでに社内にある物です。発注の項目は聞きません。'}
          </p>
        </div>
      )}

      {askAcquisitionType && editing && !showOrder && (
        <button type="button" onClick={() => setShowOrderManual(true)}
          className="text-[11px] font-semibold text-teal-700 hover:text-teal-900"
          data-testid={`${testPrefix}-add-order-info`}>
          購入情報を追加（TECHS発注の項目を表示）
        </button>
      )}

      <RequiredHints missing={missing} title={requiredTitle} testId={`${testPrefix}-required-hints`} />

      {/* 1 ── 基本情報 ─────────────────────────────────────────────── */}
      <FormSection title="基本情報" testId={`${testPrefix}-section-basic`}>
        {has('master_search') && !editing && (
          <ItemMasterSearch testId={`${testPrefix}-master-search`}
            onPick={(hit) => patch({
              name: value?.name || hit.name,
              model_no: value?.model_no || hit.model_no,
              maker: value?.maker || hit.maker,
              maker_pn: value?.maker_pn || hit.maker_pn,
              reference_url: value?.reference_url || hit.reference_url,
            })} />
        )}

        {has('name') && (
          <div>
            <Label className="text-xs font-semibold text-zinc-600">
              {FIELD_LABELS.name} {mark('name')}
            </Label>
            <Input value={value?.name || ''} onChange={set('name')} placeholder={t('reg.name.ph')}
              className="h-10 mt-1" data-testid={`${testPrefix}-name`} />
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          {has('category') && (
            <div>
              <Label className="text-xs font-semibold text-zinc-600">
                {FIELD_LABELS.category} {mark('category')}
              </Label>
              <CategoryPicker value={value?.category} options={categories}
                onChange={(v) => patch({ category: v })} testId={`${testPrefix}-category`} />
            </div>
          )}
          {has('kind') && kindOptions.length > 0 && (
            <div>
              <Label className="text-xs font-semibold text-zinc-600">{FIELD_LABELS.kind}</Label>
              <select value={value?.kind || kindOptions[0]?.value} onChange={set('kind')}
                className="w-full h-10 mt-1 rounded-md border border-zinc-200 bg-white px-3 text-sm outline-none focus:border-teal-400"
                data-testid={`${testPrefix}-kind`}>
                {kindOptions.map((k) => (
                  <option key={k.value} value={k.value}>{k.label || t(k.key)}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          {has('maker') && (
            <div>
              <Label className="text-xs font-semibold text-zinc-600">
                {FIELD_LABELS.maker} {mark('maker')}
              </Label>
              <MakerPicker value={value?.maker} onChange={(v) => patch({ maker: v })}
                testId={`${testPrefix}-maker`} />
            </div>
          )}
          {has('serial_no') && (
            <div>
              <Label className="text-xs font-semibold text-zinc-600">
                {FIELD_LABELS.serial_no} {mark('serial_no')}
              </Label>
              <Input value={value?.serial_no || ''} onChange={set('serial_no')}
                placeholder={t('reg.serial.ph')} className="h-10 mt-1 font-mono"
                data-testid={`${testPrefix}-serial`} />
            </div>
          )}
        </div>

        {has('notes') && (
          <div>
            <Label className="text-xs font-semibold text-zinc-600">{FIELD_LABELS.notes}</Label>
            <Textarea value={value?.description || ''} onChange={set('description')} rows={2}
              className="mt-1" data-testid={`${testPrefix}-notes`} />
          </div>
        )}

        {/* 台帳だけの項目（カテゴリの別マスタ・種別など）はここに差し込む。
            見出しと並びは全台帳で同じまま保つ。 */}
        {basicExtra}
      </FormSection>

      {/* 2 ── TECHS発注に必要な情報（部品は `TechsOrderSection` と共有） ──── */}
      <TechsOrderSection
        value={value} onChange={onChange} editing={editing}
        fields={effFields} requiredKeys={effRequired}
        register={register} appCode={appCode} currentNo={currentNo}
        testPrefix={testPrefix} onNumberIssued={onNumberIssued} deptCode={deptCode}
        title={techsTitle || (showOrder ? undefined : '番号')}
        note={techsNote || (showOrder ? undefined
          : '発注の項目（品番グループ・受注分類・単位・仕入先・注文URL・単価）は、これから購入する物のときだけ出ます。')}
        labels={{ number: FIELD_LABELS.number, sku_group: FIELD_LABELS.sku_group,
          order_category: FIELD_LABELS.order_category, model_no: FIELD_LABELS.model_no,
          maker_pn: FIELD_LABELS.maker_pn, uom: FIELD_LABELS.uom, qty: FIELD_LABELS.qty,
          supplier: FIELD_LABELS.supplier, reference_url: FIELD_LABELS.reference_url }} />

      {askAcquisitionType && showOrder && (
        <TechsReadinessPanel value={value} register={register} deptCode={deptCode}
          context={techsContext} departmentName={departmentName}
          testId={`${testPrefix}-techs-check`}
          onDefaults={(d) => {
            // 受注分類は既定を入れておく（あとから選び直せる）
            if (!value?.order_category_id && d?.order_category_default?.id) {
              patch({ order_category_id: d.order_category_default.id,
                order_category_name: d.order_category_default.name });
            }
          }} />
      )}

      {/* 3 ── 保管・管理 ──────────────────────────────────────────── */}
      <FormSection title="保管・管理" testId={`${testPrefix}-section-keeping`}>
        {has('location') && (
          <div>
            <Label className="text-xs font-semibold text-zinc-600">
              {FIELD_LABELS.location} {mark('location')}
            </Label>
            <LocationPicker value={value?.location_id || ''} legacyText={value?.location || ''}
              departmentId={locationDepartmentId}
              preferDeptCode={deptCode}
              onChange={(id, row) => patch({
                location_id: id,
                location: id ? (row?.name || '') : (value?.location || ''),
              })}
              testId={`${testPrefix}-location`} />
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          {has('department') && (
            <div>
              <Label className="text-xs font-semibold text-zinc-600">
                {FIELD_LABELS.department} {mark('department')}
              </Label>
              <select value={value?.department_id || ''} onChange={set('department_id')}
                className="w-full h-10 mt-1 rounded-md border border-zinc-200 bg-white px-3 text-sm outline-none focus:border-teal-400"
                data-testid={`${testPrefix}-department`}>
                <option value="">{t('reg.unassigned')}</option>
                {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
          )}
          {has('status') && statusOptions.length > 0 && (
            <div>
              <Label className="text-xs font-semibold text-zinc-600">{FIELD_LABELS.status}</Label>
              <select value={value?.status || ''} onChange={set('status')}
                className="w-full h-10 mt-1 rounded-md border border-zinc-200 bg-white px-3 text-sm outline-none focus:border-teal-400"
                data-testid={`${testPrefix}-status`}>
                {statusOptions.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          {has('owner') && (
            <div>
              <Label className="text-xs font-semibold text-zinc-600">{FIELD_LABELS.owner}</Label>
              <EmployeePicker value={value?.owner || ''} employees={employees}
                onChange={(v) => patch({ owner: v })} testId={`${testPrefix}-owner`} />
            </div>
          )}
          {has('acquired_on') && (
            <div>
              <Label className="text-xs font-semibold text-zinc-600">{FIELD_LABELS.acquired_on}</Label>
              <DateField value={value?.acquired_on || ''} onChange={(v) => patch({ acquired_on: v })}
                className="mt-1" inputClassName="h-10" testId={`${testPrefix}-acquired-on`} />
            </div>
          )}
          {has('price') && (
            <div>
              <Label className="text-xs font-semibold text-zinc-600">{FIELD_LABELS.price}</Label>
              <Input type="number" min="0" inputMode="decimal" value={value?.acquisition_price ?? ''}
                onChange={set('acquisition_price')} placeholder={t('reg.price.ph')}
                className="h-10 mt-1" data-testid={`${testPrefix}-price`} />
            </div>
          )}
        </div>

        {has('attachments') && onUpload && (
          <AttachmentsField files={files} onChange={onFilesChange} onUpload={onUpload}
            testPrefix={`${testPrefix}-supplementary`} />
        )}

        {keepingExtra}
      </FormSection>

      {/* 4 ── 台帳ごとの追加 ───────────────────────────────────────── */}
      {extra}
      {children}
    </div>
  );
};

export default CommonRegistrationBlock;
